import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE } from '../services/apiBase';

const API = API_BASE;
const TENANT = '569593dd-38e2-4d69-bdca-5d07be8b46f1';

const GENRES = ['Any','Afrikaans','English','Golden Oldies','Country','Gospel','CCM','Dance','Christmas','Jazz','RnB','Pop','Rock','Instrumental','Classical','Worship','Kids','Other'];
const COLORS = ['#22c55e','#3b82f6','#f59e0b','#ef4444','#a78bfa','#ec4899','#14b8a6','#f97316'];

function fmt(s) {
  if (!s) return '0:00';
  const h = Math.floor(s/3600), m = Math.floor((s%3600)/60), sec = Math.floor(s%60);
  if (h > 0) return h+'h '+m+'m';
  return m+'m '+sec+'s';
}

function GenrePill({ genre, count, color, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
      border: '1px solid ' + color + '40', background: color + '18',
      color: color, cursor: 'pointer', whiteSpace: 'nowrap'
    }}>{genre} {count > 0 && <span style={{opacity:0.6}}>({count})</span>}</button>
  );
}

function RuleRow({ rule, index, onChange, onRemove, trackCounts }) {
  const count = trackCounts[rule.genre] || 0;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
      <div style={{ width:4, height:36, borderRadius:2, background: COLORS[index % COLORS.length], flexShrink:0 }} />
      <select value={rule.genre} onChange={e => onChange(index, 'genre', e.target.value)}
        style={{ flex:1, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:6, padding:'6px 10px', color:'#e5e7eb', fontSize:13 }}>
        {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
      </select>
      <div style={{ display:'flex', alignItems:'center', gap:6, width:160 }}>
        <input type="range" min={0} max={100} value={rule.percentage}
          onChange={e => onChange(index, 'percentage', parseInt(e.target.value))}
          style={{ flex:1, accentColor: COLORS[index % COLORS.length] }} />
        <span style={{ fontSize:14, fontWeight:700, color: COLORS[index % COLORS.length], minWidth:36 }}>{rule.percentage}%</span>
      </div>
      <span style={{ fontSize:11, color:'#6b7280', minWidth:60 }}>{count} tracks</span>
      <button onClick={() => onRemove(index)} style={{ width:28, height:28, borderRadius:6, border:'1px solid rgba(239,68,68,0.3)', background:'rgba(239,68,68,0.08)', color:'#ef4444', cursor:'pointer', fontSize:14 }}>×</button>
    </div>
  );
}

function PlaylistCard({ playlist, onSelect, onDelete, selected }) {
  return (
    <div onClick={() => onSelect(playlist)} style={{
      padding:'14px 16px', borderRadius:10, cursor:'pointer',
      border: '1px solid ' + (selected ? playlist.color : 'rgba(255,255,255,0.08)'),
      background: selected ? playlist.color + '12' : 'rgba(255,255,255,0.02)',
      transition:'all 0.15s'
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:10, height:10, borderRadius:'50%', background:playlist.color, flexShrink:0 }} />
          <div>
            <div style={{ fontSize:14, fontWeight:700, color:'#f1f5f9' }}>{playlist.name}</div>
            <div style={{ fontSize:11, color:'#6b7280', marginTop:2 }}>
              {playlist.track_count || 0} tracks · {fmt(playlist.total_duration)}
            </div>
          </div>
        </div>
        <button onClick={e => { e.stopPropagation(); onDelete(playlist.id); }}
          style={{ width:24, height:24, borderRadius:5, border:'none', background:'transparent', color:'#4b5563', cursor:'pointer', fontSize:14 }}>×</button>
      </div>
      {playlist.description && <div style={{ fontSize:11, color:'#6b7280', marginTop:6, marginLeft:20 }}>{playlist.description}</div>}
    </div>
  );
}

export default function PlaylistBuilder() {
  const [playlists, setPlaylists] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [allTracks, setAllTracks] = useState([]);
  const [rules, setRules] = useState([]);
  const [totalTracks, setTotalTracks] = useState(20);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(COLORS[0]);
  const [newDesc, setNewDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [building, setBuilding] = useState(false);
  const [msg, setMsg] = useState('');
  const [activeTab, setActiveTab] = useState('rules'); // rules | tracks
  const [search, setSearch] = useState('');
  const [trackSearch, setTrackSearch] = useState('');
  const token = localStorage.getItem('access_token');
  const headers = { Authorization: 'Bearer ' + token };

  const trackCounts = {};
  allTracks.forEach(t => {
    const g = t.genre || 'Unknown';
    trackCounts[g] = (trackCounts[g] || 0) + 1;
    trackCounts['Any'] = (trackCounts['Any'] || 0) + 1;
  });

  const loadPlaylists = useCallback(() => {
    axios.get(API + '/playlists', { params: { tenant_id: TENANT }, headers })
      .then(r => setPlaylists(r.data || [])).catch(console.error);
  }, []);

  const loadAllTracks = useCallback(() => {
    axios.get(API + '/music/tracks', { params: { tenant_id: TENANT }, headers })
      .then(r => setAllTracks(r.data || [])).catch(console.error);
  }, []);

  useEffect(() => { loadPlaylists(); loadAllTracks(); }, []);

  const selectPlaylist = async (pl) => {
    setSelected(pl);
    setActiveTab('rules');
    try {
      const r = await axios.get(API + '/playlists/' + pl.id + '/tracks', { headers });
      setTracks(r.data || []);
    } catch(e) { setTracks([]); }
  };

  const createPlaylist = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const r = await axios.post(API + '/playlists', {
        tenant_id: TENANT, name: newName, description: newDesc, color: newColor
      }, { headers });
      setCreating(false); setNewName(''); setNewDesc('');
      loadPlaylists();
      setMsg('✅ Playlist created!');
      setTimeout(() => setMsg(''), 3000);
    } catch(e) { setMsg('❌ Failed to create'); }
    setSaving(false);
  };

  const deletePlaylist = async (id) => {
    if (!window.confirm('Delete this playlist?')) return;
    await axios.delete(API + '/playlists/' + id, { headers });
    if (selected?.id === id) setSelected(null);
    loadPlaylists();
  };

  const autoBuild = async () => {
    if (!selected) return;
    const total = rules.reduce((s, r) => s + r.percentage, 0);
    if (total !== 100) { setMsg('❌ Percentages must add up to 100%'); return; }
    setBuilding(true);
    setMsg('Building playlist...');
    try {
      const r = await axios.post(API + '/playlists/' + selected.id + '/auto-build', {
        tenant_id: TENANT, rules, total_tracks: totalTracks
      }, { headers });
      setMsg('✅ Built ' + r.data.tracks_added + ' tracks!');
      setTimeout(() => setMsg(''), 4000);
      const tr = await axios.get(API + '/playlists/' + selected.id + '/tracks', { headers });
      setTracks(tr.data || []);
      loadPlaylists();
    } catch(e) { setMsg('❌ Build failed'); }
    setBuilding(false);
  };

  const removeTrack = async (trackId) => {
    await axios.delete(API + '/playlists/' + selected.id + '/tracks/' + trackId, { headers });
    setTracks(t => t.filter(x => x.id !== trackId));
    loadPlaylists();
  };

  const addTrack = async (track) => {
    try {
      await axios.post(API + '/playlists/' + selected.id + '/tracks', { track_id: track.id }, { headers });
      const r = await axios.get(API + '/playlists/' + selected.id + '/tracks', { headers });
      setTracks(r.data || []);
      loadPlaylists();
    } catch(e) { console.error(e); }
  };

  const addRule = () => setRules(r => [...r, { genre: 'Any', percentage: 0 }]);
  const updateRule = (i, k, v) => setRules(r => r.map((x, j) => j===i ? {...x, [k]:v} : x));
  const removeRule = (i) => setRules(r => r.filter((_, j) => j !== i));
  const totalPct = rules.reduce((s, r) => s + r.percentage, 0);

  const filteredAll = allTracks.filter(t => {
    const inPlaylist = tracks.some(pt => pt.id === t.id);
    if (inPlaylist) return false;
    const q = trackSearch.toLowerCase();
    return !q || (t.title||'').toLowerCase().includes(q) || (t.artist||'').toLowerCase().includes(q);
  });

  return (
    <div style={{ display:'flex', height:'100vh', background:'#080a0f', color:'#e2e8f0', fontFamily:'system-ui,sans-serif', overflow:'hidden' }}>

      {/* Left: Playlist list */}
      <div style={{ width:260, borderRight:'1px solid rgba(255,255,255,0.07)', display:'flex', flexDirection:'column', flexShrink:0 }}>
        <div style={{ padding:'16px 16px 12px', borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
            <span style={{ fontSize:15, fontWeight:800, color:'#fff', letterSpacing:-0.3 }}>Playlists</span>
            <button onClick={() => setCreating(true)}
              style={{ width:28, height:28, borderRadius:7, border:'none', background:'#22c55e', color:'#000', cursor:'pointer', fontSize:18, fontWeight:700, lineHeight:'28px' }}>+</button>
          </div>
          <input placeholder="Search playlists..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:7, padding:'5px 10px', color:'#e2e8f0', fontSize:12, boxSizing:'border-box' }} />
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'8px 12px', display:'flex', flexDirection:'column', gap:8 }}>
          {playlists.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase())).map(pl => (
            <PlaylistCard key={pl.id} playlist={pl} onSelect={selectPlaylist} onDelete={deletePlaylist} selected={selected?.id === pl.id} />
          ))}
          {playlists.length === 0 && (
            <div style={{ padding:'40px 0', textAlign:'center', color:'#374151', fontSize:13 }}>
              <div style={{ fontSize:32, marginBottom:8 }}>📋</div>
              No playlists yet
            </div>
          )}
        </div>
      </div>

      {/* Main */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
        {!selected && !creating ? (
          <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:16, color:'#374151' }}>
            <div style={{ fontSize:56 }}>🎵</div>
            <div style={{ fontSize:16, fontWeight:600 }}>Select a playlist or create one</div>
            <button onClick={() => setCreating(true)}
              style={{ padding:'10px 24px', borderRadius:8, border:'none', background:'#22c55e', color:'#000', cursor:'pointer', fontSize:14, fontWeight:700 }}>
              + New Playlist
            </button>
          </div>
        ) : creating ? (
          <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <div style={{ width:420, background:'rgba(255,255,255,0.03)', borderRadius:14, border:'1px solid rgba(255,255,255,0.08)', padding:28 }}>
              <h2 style={{ margin:'0 0 20px', fontSize:18, fontWeight:800, color:'#fff' }}>New Playlist</h2>
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:5 }}>NAME</label>
                <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Morning Vibes"
                  style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, padding:'8px 12px', color:'#e2e8f0', fontSize:14, boxSizing:'border-box' }} />
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:5 }}>DESCRIPTION</label>
                <input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Optional description..."
                  style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, padding:'8px 12px', color:'#e2e8f0', fontSize:14, boxSizing:'border-box' }} />
              </div>
              <div style={{ marginBottom:20 }}>
                <label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:8 }}>COLOR</label>
                <div style={{ display:'flex', gap:8 }}>
                  {COLORS.map(c => (
                    <button key={c} onClick={() => setNewColor(c)} style={{
                      width:28, height:28, borderRadius:'50%', background:c, border: newColor===c ? '2px solid #fff' : '2px solid transparent', cursor:'pointer'
                    }} />
                  ))}
                </div>
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={() => setCreating(false)}
                  style={{ flex:1, padding:'9px', borderRadius:8, border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#9ca3af', cursor:'pointer', fontSize:13 }}>Cancel</button>
                <button onClick={createPlaylist} disabled={saving || !newName.trim()}
                  style={{ flex:2, padding:'9px', borderRadius:8, border:'none', background: newName.trim() ? newColor : '#374151', color:'#000', cursor: newName.trim() ? 'pointer' : 'not-allowed', fontSize:13, fontWeight:700 }}>
                  {saving ? 'Creating...' : 'Create Playlist'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ padding:'14px 24px', borderBottom:'1px solid rgba(255,255,255,0.07)', background:'rgba(255,255,255,0.01)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:12, height:12, borderRadius:'50%', background:selected.color }} />
                <div>
                  <div style={{ fontSize:18, fontWeight:800, color:'#fff', letterSpacing:-0.3 }}>{selected.name}</div>
                  <div style={{ fontSize:12, color:'#6b7280', marginTop:1 }}>{tracks.length} tracks · {fmt(tracks.reduce((s,t) => s+(t.duration_seconds||0),0))}</div>
                </div>
              </div>
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                {msg && <span style={{ fontSize:12, color: msg.startsWith('✅') ? '#22c55e' : msg.startsWith('❌') ? '#ef4444' : '#f59e0b' }}>{msg}</span>}
                <div style={{ display:'flex', borderRadius:8, overflow:'hidden', border:'1px solid rgba(255,255,255,0.08)' }}>
                  {['rules','tracks'].map(tab => (
                    <button key={tab} onClick={() => setActiveTab(tab)} style={{
                      padding:'6px 16px', border:'none', cursor:'pointer', fontSize:12, fontWeight:600, textTransform:'capitalize',
                      background: activeTab===tab ? 'rgba(255,255,255,0.1)' : 'transparent',
                      color: activeTab===tab ? '#fff' : '#6b7280'
                    }}>{tab === 'rules' ? '⚙️ Rules' : '🎵 Tracks (' + tracks.length + ')'}</button>
                  ))}
                </div>
              </div>
            </div>

            {activeTab === 'rules' ? (
              <div style={{ flex:1, overflowY:'auto', padding:'20px 24px' }}>
                {/* Auto-build controls */}
                <div style={{ background:'rgba(255,255,255,0.02)', borderRadius:12, border:'1px solid rgba(255,255,255,0.07)', padding:20, marginBottom:20 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'#9ca3af', letterSpacing:1, marginBottom:14 }}>AUTO-BUILD RULES</div>
                  <div style={{ fontSize:12, color:'#6b7280', marginBottom:16 }}>
                    Define genre mix percentages. Click <strong style={{color:'#9ca3af'}}>Auto-Build</strong> to populate the playlist from your library.
                  </div>

                  {rules.map((rule, i) => (
                    <RuleRow key={i} rule={rule} index={i} onChange={updateRule} onRemove={removeRule} trackCounts={trackCounts} />
                  ))}

                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:12 }}>
                    <button onClick={addRule}
                      style={{ padding:'6px 14px', borderRadius:7, border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#9ca3af', cursor:'pointer', fontSize:12 }}>
                      + Add Rule
                    </button>
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                      <span style={{ fontSize:13, color: totalPct === 100 ? '#22c55e' : totalPct > 100 ? '#ef4444' : '#f59e0b', fontWeight:700 }}>
                        {totalPct}% / 100%
                      </span>
                      {totalPct !== 100 && totalPct > 0 && (
                        <button onClick={() => {
                          const diff = 100 - totalPct;
                          if (rules.length > 0) updateRule(rules.length-1, 'percentage', rules[rules.length-1].percentage + diff);
                        }} style={{ fontSize:11, padding:'3px 8px', borderRadius:5, border:'1px solid rgba(245,158,11,0.3)', background:'rgba(245,158,11,0.1)', color:'#f59e0b', cursor:'pointer' }}>
                          Fix to 100%
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Quick genre buttons */}
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:12, color:'#6b7280', marginBottom:10 }}>QUICK ADD GENRE</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                    {Object.entries(trackCounts).filter(([g]) => g !== 'Any').sort((a,b) => b[1]-a[1]).map(([genre, count], i) => (
                      <GenrePill key={genre} genre={genre} count={count} color={COLORS[i % COLORS.length]}
                        onClick={() => setRules(r => [...r, { genre, percentage: 0 }])} />
                    ))}
                  </div>
                </div>

                {/* Total tracks + build */}
                <div style={{ display:'flex', alignItems:'center', gap:16, padding:'16px 20px', background:'rgba(34,197,94,0.05)', borderRadius:10, border:'1px solid rgba(34,197,94,0.15)' }}>
                  <div style={{ flex:1 }}>
                    <label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:6 }}>TOTAL TRACKS TO BUILD</label>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <input type="range" min={5} max={100} step={5} value={totalTracks}
                        onChange={e => setTotalTracks(parseInt(e.target.value))}
                        style={{ flex:1, accentColor:'#22c55e' }} />
                      <span style={{ fontSize:18, fontWeight:800, color:'#22c55e', minWidth:32 }}>{totalTracks}</span>
                    </div>
                  </div>
                  <button onClick={autoBuild} disabled={building || rules.length === 0 || totalPct !== 100}
                    style={{ padding:'10px 24px', borderRadius:9, border:'none', background: (building || rules.length===0 || totalPct!==100) ? '#374151' : '#22c55e', color: (building || rules.length===0 || totalPct!==100) ? '#6b7280' : '#000', cursor: (building || rules.length===0 || totalPct!==100) ? 'not-allowed' : 'pointer', fontSize:14, fontWeight:700, whiteSpace:'nowrap' }}>
                    {building ? '⟳ Building...' : '⚡ Auto-Build'}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ flex:1, display:'flex', overflow:'hidden' }}>
                {/* Current playlist tracks */}
                <div style={{ flex:1, overflowY:'auto', padding:'16px 20px', borderRight:'1px solid rgba(255,255,255,0.07)' }}>
                  <div style={{ fontSize:12, color:'#6b7280', marginBottom:12, fontWeight:700, letterSpacing:1 }}>
                    PLAYLIST TRACKS ({tracks.length})
                  </div>
                  {tracks.length === 0 ? (
                    <div style={{ padding:'40px 0', textAlign:'center', color:'#374151', fontSize:13 }}>
                      No tracks yet — use Auto-Build or add manually
                    </div>
                  ) : tracks.map((t, i) => (
                    <div key={t.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                      <span style={{ fontSize:11, color:'#4b5563', minWidth:22, textAlign:'right' }}>{i+1}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:600, color:'#e2e8f0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.title || t.original_filename?.replace(/\.mp3$/i,'') || 'Untitled'}</div>
                        <div style={{ fontSize:11, color:'#6b7280' }}>{t.artist || '—'} · {t.genre || '—'}</div>
                      </div>
                      {t.bpm && <span style={{ fontSize:10, padding:'2px 6px', borderRadius:4, background:'rgba(56,189,248,0.1)', color:'#38bdf8' }}>{t.bpm} BPM</span>}
                      <span style={{ fontSize:11, color:'#6b7280', minWidth:36 }}>{Math.floor((t.duration_seconds||0)/60)}:{String(Math.floor((t.duration_seconds||0)%60)).padStart(2,'0')}</span>
                      <button onClick={() => removeTrack(t.id)}
                        style={{ width:24, height:24, borderRadius:5, border:'none', background:'transparent', color:'#4b5563', cursor:'pointer', fontSize:14 }}>×</button>
                    </div>
                  ))}
                </div>

                {/* Add tracks panel */}
                <div style={{ width:300, overflowY:'auto', padding:'16px 16px' }}>
                  <div style={{ fontSize:12, color:'#6b7280', marginBottom:10, fontWeight:700, letterSpacing:1 }}>ADD TRACKS</div>
                  <input placeholder="Search library..." value={trackSearch} onChange={e => setTrackSearch(e.target.value)}
                    style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:7, padding:'6px 10px', color:'#e2e8f0', fontSize:12, boxSizing:'border-box', marginBottom:10 }} />
                  {filteredAll.slice(0,50).map(t => (
                    <div key={t.id} onClick={() => addTrack(t)} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0', borderBottom:'1px solid rgba(255,255,255,0.04)', cursor:'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.03)'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:12, fontWeight:600, color:'#e2e8f0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.title || t.original_filename?.replace(/\.mp3$/i,'') || 'Untitled'}</div>
                        <div style={{ fontSize:10, color:'#6b7280' }}>{t.artist || '—'} · {t.genre || '—'}</div>
                      </div>
                      <span style={{ fontSize:18, color:'#374151' }}>+</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
